/**
 * MatriculePrintScreen — the matricule / vignette print surface (R-10, gated sampling.matricule.print).
 *
 *  - Builds the print payload via POST /sampling/matricule/vignette, then renders the human strip +
 *    QR payload and drives window.print() (the print CSS isolates `.smp-print-area`).
 *  - Full 5-state DoD: route-level forbidden (the module nav item carries the print cap), offline,
 *    plus the form's own empty/error feedback via toast.
 *  - Keyboard-first: Enter in any field generates; the print button is a real <button>.
 */
import { useState } from "react";
import "../sampling.css";
import { Button, Card, EmptyState, FormField, Input, OfflineState, useToast } from "@/ui";
import { useT, useUiStore } from "@/app";
import { type ApiError } from "@/api";
import { token } from "@/design/theme";
import { useVignette } from "../hooks";
import type { Vignette } from "../types";
import { VignettePreview } from "../components/VignettePreview";

interface FormState {
  matricule: string;
  token: string;
  nom: string;
  prenom: string;
  demande_ref: string;
}

const empty: FormState = { matricule: "", token: "", nom: "", prenom: "", demande_ref: "" };

export function MatriculePrintScreen() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);

  const [form, setForm] = useState<FormState>(empty);
  const [vignette, setVignette] = useState<Vignette | null>(null);

  const build = useVignette();

  const patch = (next: Partial<FormState>) => setForm((f) => ({ ...f, ...next }));
  const hasInput = Object.values(form).some((v) => v.trim() !== "");

  const generate = () => {
    if (!hasInput) return;
    const body: Record<string, string> = {};
    if (form.matricule.trim()) body.matricule = form.matricule.trim();
    if (form.token.trim()) body.token = form.token.trim();
    if (form.nom.trim()) body.nom = form.nom.trim();
    if (form.prenom.trim()) body.prenom = form.prenom.trim();
    if (form.demande_ref.trim()) body.demande_ref = form.demande_ref.trim();

    build.mutate(body, {
      onSuccess: (v) => {
        setVignette(v);
        toast.success(t("sampling.print.generated"));
      },
      onError: (e: ApiError) => toast.error(e.message, e.code),
    });
  };

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      generate();
    }
  };

  if (!online) return <OfflineState />;

  return (
    <div className="smp-page">
      <header className="smp-head">
        <h1 className="smp-head__title">{t("sampling.print.title")}</h1>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 22rem)",
          gap: token.s5,
          alignItems: "start",
        }}
      >
        <Card title={t("sampling.print.title")}>
          <p style={{ marginBlockStart: 0, color: token.slate }}>{t("sampling.print.subtitle")}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
            <FormField label={t("sampling.print.matricule")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  value={form.matricule}
                  onChange={(e) => patch({ matricule: e.target.value })}
                  onKeyDown={onEnter}
                />
              )}
            </FormField>
            <FormField label={t("sampling.print.token")} hint={t("sampling.print.qr", { t: "{v,t}" })}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  value={form.token}
                  onChange={(e) => patch({ token: e.target.value })}
                  onKeyDown={onEnter}
                />
              )}
            </FormField>
            <div className="smp-draw__grid">
              <FormField label={t("sampling.print.nom")}>
                {(p) => (
                  <Input
                    {...p}
                    value={form.nom}
                    onChange={(e) => patch({ nom: e.target.value })}
                    onKeyDown={onEnter}
                  />
                )}
              </FormField>
              <FormField label={t("sampling.print.prenom")}>
                {(p) => (
                  <Input
                    {...p}
                    value={form.prenom}
                    onChange={(e) => patch({ prenom: e.target.value })}
                    onKeyDown={onEnter}
                  />
                )}
              </FormField>
            </div>
            <FormField label={t("sampling.print.demande")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  value={form.demande_ref}
                  onChange={(e) => patch({ demande_ref: e.target.value })}
                  onKeyDown={onEnter}
                />
              )}
            </FormField>

            <div className="smp-actions">
              <Button variant="primary" onClick={generate} loading={build.isPending} disabled={!hasInput}>
                {t("sampling.print.generate")}
              </Button>
              {vignette && (
                <Button variant="secondary" onClick={() => window.print()} leadingIcon={<span aria-hidden>⎙</span>}>
                  {t("sampling.print.print")}
                </Button>
              )}
            </div>
          </div>
        </Card>

        <Card title={t("sampling.print.previewTitle")}>
          {vignette ? (
            <VignettePreview vignette={vignette} />
          ) : (
            <EmptyState glyph="▢" title={t("sampling.print.previewTitle")} description={t("sampling.print.empty")} />
          )}
        </Card>
      </div>
    </div>
  );
}
